import { Link, useNavigate } from '@tanstack/react-router';
import {
  Language,
  languagesToIconMap,
  languagesToISOAbbreviationMap,
  languagesToLabelMap,
  RouterKey,
} from 'consts';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { motion } from 'motion/react';
import React, { FC, useCallback, useState } from 'react';

import { socialButtonsFixtures } from './Footer';
import { CloseIcon } from './icons/CloseIcon';
import { MaazeebLogo } from './icons/MaazeebLogo';
import { MenuIcon } from './icons/MenuIcon';
import { WorldIcon } from './icons/WorldIcon';
import { Button } from './ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';

export interface NavFixture {
  title: string;
  href: string;
}

export const navFixtures: NavFixture[] = [
  { title: StringKey.NAV_PRODUCT, href: RouterKey.HOME_PAGE + '#product' },
  { title: StringKey.NAV_FEATURES, href: RouterKey.HOME_PAGE + '#features' },
  { title: StringKey.NAV_FAQS, href: RouterKey.HOME_PAGE + '#faqs' },
  { title: StringKey.NAV_CONTACT_US, href: RouterKey.HOME_PAGE + '#contactus' },
];

interface HeaderProps {
  minified?: boolean;
}

export const Header: FC<HeaderProps> = ({ minified = false }) => {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);

  const handleLanguage = useCallback(
    (lang: Language) => {
      setLanguage(lang);
      setIsOpen(false);
    },
    [setLanguage],
  );

  return (
    <motion.header
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0, transition: { ease: 'easeOut', duration: 0.6 } }}
      className={cn(
        'fixed top-0 z-20 w-full h-20 flex px-4 sm:px-16 md:px-28 py-4 justify-between items-center border-b border-[#171221] bg-[#0B0910]',
        minified && 'justify-center',
      )}
    >
      {isMenuOpened && !minified && (
        <div className="fixed top-20 left-0 pt-6 z-4 w-full h-full bg-[#0B0910]">
          <div className="relative z-1 flex flex-col">
            {navFixtures.map((item, index) => (
              <Button
                key={index}
                variant="secondary"
                onClick={() => navigate({ to: item.href })}
                className="justify-start px-4 py-3 bg-transparent border-transparent rounded-none border-0 hover:border-0"
              >
                {t(item.title as StringKey)}
              </Button>
            ))}
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate({ to: RouterKey.REQUEST_DEMO as string })}
            className="relative z-1 w-[calc(100%-32px)] py-3 mx-4 mt-4 border-[#B097E9] font-semibold"
          >
            <p className="bg-clip-text bg-[linear-gradient(91.39deg,#B097E9_8.09%,#6B9BC7_152.31%)] text-transparent">
              {t(StringKey.REQUEST_A_DEMO)}
            </p>
          </Button>
          <div className="relative z-1 h-[24px] my-10 flex items-center justify-center">
            <img
              className="w-fit h-full block object-cover"
              src="/assets/footer-divider.png"
              alt="Footer divider"
            />
          </div>
          <div className="relative z-1 flex flex-col my-6 mx-4 gap-4">
            <div className="flex items-center gap-3">
              {socialButtonsFixtures.map((item) => (
                <a
                  key={item.link}
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  className="border border-transparent bg-origin-border bg-clip-border bg-[linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0.06)_100%)] rounded-lg"
                >
                  <div className="p-2 bg-[linear-gradient(144.42deg,#171221_0%,#0B0910_100%)] rounded-lg">
                    {item.icon}
                  </div>
                </a>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-start gap-4 select-none">
              {/* TODO: Enable app download links when available
                <>
                  <a href="/">
                    <AppStoreBadge />
                  </a>
                  <a href="/">
                    <GooglePlayBadge />
                  </a>
                </>
              */}
            </div>
          </div>
          <div className="absolute z-0 left-0 bottom-20 w-full overflow-hidden">
            <img
              className="relative z-0 left-0 w-full object-cover"
              src="/assets/menu-bg.png"
              alt="menu-background"
            />
          </div>
        </div>
      )}
      <div className={cn(!minified && 'w-0')}>
        <Link to={RouterKey.HOME_PAGE + '#product'}>
          <MaazeebLogo />
        </Link>
      </div>
      <div className={cn('hidden lg:flex items-center justify-center', minified && 'lg:hidden')}>
        {navFixtures.map((item, index) => (
          <Button
            key={index}
            variant="secondary"
            onClick={() => navigate({ to: item.href })}
            className="px-3.5 py-2 xl:px-5 xl:py-3 bg-transparent border-transparent"
          >
            {t(item.title as StringKey)}
          </Button>
        ))}
      </div>
      <div className={cn('flex w-0 items-center justify-end gap-2 xl:gap-3', minified && 'hidden')}>
        <Popover onOpenChange={setIsOpen} open={isOpen}>
          <PopoverTrigger>
            <Button
              variant="secondary"
              className={cn(
                'px-3.5 py-2 xl:px-5 xl:py-3 flex items-center gap-2 text-sm xs:text-m font-inter',
                !isOpen && 'bg-transparent border-transparent',
              )}
            >
              <WorldIcon />
              <div>{languagesToISOAbbreviationMap[language].toUpperCase()}</div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[200px] mt-1 bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)]"
          >
            {Object.values(Language).map((lang, index) => (
              <button
                key={lang}
                className={cn(
                  'relative w-full p-3 flex items-center gap-2 text-sm font-medium justify-start',
                  index === 0 && 'rounded-t-lg',
                  index === Object.values(Language).length - 1 && 'rounded-b-lg',
                  lang === language &&
                    'bg-[#202020] shadow-[inset_0px_2px_6px_0px_rgba(255,255,255,0.15)]',
                  //TODO: remove this after client changes
                  //lang !== Language.ENGLISH && 'pointer-events-none',
                )}
                style={{
                  fontFamily:
                    lang === Language.ARABIC ? 'TheSansArab, sans-serif' : 'Inter, sans-serif',
                }}
                onClick={() => handleLanguage(lang)}
              >
                {/* TODO: remove options disabling and 'Soon' text after client changes */}
                <div /*className={lang !== Language.ENGLISH ? 'opacity-20' : ''}*/>
                  {languagesToIconMap[lang] && React.createElement(languagesToIconMap[lang], {})}
                </div>
                <div /*className={lang !== Language.ENGLISH ? 'opacity-20' : ''}*/>
                  {languagesToLabelMap[lang]}
                </div>
                {/*lang !== Language.ENGLISH && (
                  <p className="absolute w-[calc(100%-24px)] text-end font-inter">Soon</p>
                )*/}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <Button
          variant="secondary"
          onClick={() => navigate({ to: RouterKey.REQUEST_DEMO as string })}
          className="hidden lg:flex whitespace-nowrap px-3.5 py-2 xl:px-[22px] xl:py-3 xl:h-12 border-[#B097E9] text-m font-semibold"
        >
          <p className="bg-clip-text bg-[linear-gradient(91.39deg,#B097E9_8.09%,#6B9BC7_152.31%)] text-transparent">
            {t(StringKey.REQUEST_A_DEMO)}
          </p>
        </Button>
        <div className="flex lg:hidden">
          <button onClick={() => setIsMenuOpened((prev) => !prev)}>
            {isMenuOpened ? <CloseIcon width={18} fill={'#ffffff'} /> : <MenuIcon />}
          </button>
        </div>
      </div>
    </motion.header>
  );
};
